import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import config from '../config/config';
import i18n from '../i18n/i18n';
import { translateServerMessage } from '../i18n/serverMessages';
import './ResultPage.css';

// For text set inside validateTicket, whose dependencies should not change with the language.
const st = (key, options) => i18n.t(key, { ns: 'scanner', ...options });

// An error whose message is ready to show (a server message, translated, or one of ours). `raw` is
// the English it was made from, which the connection-help check below still reads.
const displayError = (message, raw) => Object.assign(new Error(message), { display: true, raw });

// Create module-level variables to track API calls
let apiCallInProgress = false;
let lastProcessedValue = null;

const ResultPage = ({ result, eventId }) => {
  const { t } = useTranslation('scanner');
  const navigate = useNavigate();
  const [validationData, setValidationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // The untranslated text behind `error`.
  const rawErrorRef = useRef(null);
  const requestMadeRef = useRef(false);
  const resultRef = useRef(null);
  const apiCallMadeRef = useRef(false);
  const { getAccessToken } = useAuth();
  const [shouldRetry, setShouldRetry] = useState(false);
  
  // Extract iboNumber from scan result or detect new QR format
  const getIboNumber = (result) => {
    // Check if this is the new QR code format starting with "T-" or "GT-"
    if (result && (result.startsWith('T-') || result.startsWith('GT-'))) {
      // This is the new format, return the code after "T-" or "GT-"
      if (result.startsWith('GT-')) {
        return result.substring(3); // Remove "GT-" prefix
      } else {
        return result.substring(2); // Remove "T-" prefix
      }
    }
    
    // Old format: strip any non-numeric characters if needed
    const iboNumber = result.replace(/\D/g, '');
    return iboNumber;
  };

  // Check if the result is the new QR code format
  const isNewQRFormat = (result) => {
    return result && (result.startsWith('T-') || result.startsWith('GT-'));
  };

  // Check if the result is a generic ticket
  const isGenericTicket = (result) => {
    return result && result.startsWith('GT-');
  };

  // Immediate check to prevent processing in duplicate renders
  useEffect(() => {
    // Reset flags when component mounts with a new result
    if (result && result !== lastProcessedValue) {
      apiCallMadeRef.current = false;
    }
    
    // Store the result for comparison
    resultRef.current = result;
    
    return () => {
      // When component unmounts, reset flags
      apiCallMadeRef.current = false;
    };
  }, [result]);

  // Validate the ticket using the API - memoized with useCallback to avoid dependency issues
  const validateTicket = useCallback(async (processedResult) => {
    if (!processedResult || apiCallInProgress) return;
    
    try {
      // Set the API call in progress
      apiCallInProgress = true;
      lastProcessedValue = processedResult;
      apiCallMadeRef.current = true;
      
      setLoading(true);
      setError(null);
      
      const token = await getAccessToken();
      
      if (!token) {
        throw displayError(st('result.error.authToken'), 'Authentication token not available');
      }
      
      let url;
      
      // Determine which API endpoint to use based on QR code format
      if (isNewQRFormat(result)) {
        // New format: use ticketQRCode parameter
        const ticketQRCode = processedResult; // This is the code after "T-" or "GT-"
        const isGeneric = isGenericTicket(result);
        
        // The event the volunteer picked has to travel with the scan. Without it the server has
        // nothing to check the ticket against, so a ticket for any other event was admitted and
        // redeemed — an IBO lost November convention tickets scanned at an October seminar.
        const scanningEvent = eventId ? `&eventId=${encodeURIComponent(eventId)}` : '';

        if (isGeneric) {
          url = `${config.API_BASE_URL}/em/v1/ticket/validate?ticketQRCode=${encodeURIComponent(ticketQRCode)}&isGenericTicket=true${scanningEvent}`;
          console.log('Validating generic ticket for event', eventId, '→', url);
        } else {
          url = `${config.API_BASE_URL}/em/v1/ticket/validate?ticketQRCode=${encodeURIComponent(ticketQRCode)}${scanningEvent}`;
          console.log('Validating ticket for event', eventId, '→', url);
        }
      } else {
        // Old format: use iboNumber parameter with eventId
        url = `${config.API_BASE_URL}/em/v1/ticket/validate?iboNumber=${processedResult}${eventId ? `&eventId=${eventId}` : ''}`;
        console.log('Validating by IBO for event', eventId, '→', url);
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Parse the response JSON regardless of status code
      const data = await response.json();
      console.log(`[${new Date().toISOString()}] API Response Full:`, JSON.stringify(data, null, 2));
      
      // Check if response has an error status
      if (!response.ok) {
        // Extract the specific error message from the API response
        const errorMessage = data.message || `API error: ${response.status}`;
        throw displayError(
          translateServerMessage(data.message, st('result.error.apiStatus', { status: response.status })),
          errorMessage
        );
      }
      
      // Handle API responses with nested structures more intelligently
      let actualData = null;
      
      // Case 1: Standard success response with data in data field
      if (data.status === "success" && data.data) {
        console.log('Found standard success response with data');
        actualData = data.data;
      } 
      // Case 2: Data directly in response without status wrapper
      else if (data.data && !data.status) {
        console.log('Found data without status wrapper');
        actualData = data.data;
      }
      // Case 3: Response is the data itself (contains expected ticket fields)
      else if (data.eventName || data.iboNumber || data.valid !== undefined) {
        console.log('Response appears to be the data itself');
        actualData = data;
      }
      // Case 4: Data is in another property
      else if (typeof data === 'object') {
        // Look for likely data containers
        const possibleDataKeys = Object.keys(data).filter(key => 
          typeof data[key] === 'object' && data[key] !== null
        );
        
        if (possibleDataKeys.length) {
          console.log('Found possible data in key:', possibleDataKeys[0]);
          actualData = data[possibleDataKeys[0]];
        } else {
          // Last resort: use the whole response
          console.log('Using entire response as data');
          actualData = data;
        }
      }
      
      if (actualData) {
        console.log('Final data being set:', actualData);
        setValidationData(actualData);
        // Reset retry flag if we succeeded
        setShouldRetry(false);
      } else {
        const errorMsg = data.message || data.error || "Could not extract validation data from response";
        console.error('Data extraction error:', errorMsg);
        rawErrorRef.current = errorMsg;
        setError(translateServerMessage(data.message || data.error, st('result.error.extract')));
      }
    } catch (err) {
      console.error(`[${new Date().toISOString()}] API Error:`, err);
      
      // Display the error message directly from the Error object
      rawErrorRef.current = err.display ? err.raw : err.message;
      if (err.display) {
        setError(err.message);
      } else if (err.message) {
        // Not from the server: the browser's own text, e.g. "Failed to fetch" when the request
        // could not be made. English shows it as before; Spanish says what happened instead.
        const networkFailure = err instanceof TypeError && /fetch|network|load failed/i.test(err.message);
        setError(st(networkFailure ? 'result.error.network' : 'result.error.unexpected', { message: err.message }));
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
      apiCallInProgress = false;
    }
  }, [getAccessToken, eventId, result]);

  // Split the initialization of validation into a separate useEffect to avoid race conditions
  useEffect(() => {
    // Immediately return if not eligible for processing
    if (!result || 
        !resultRef.current || 
        result !== resultRef.current || 
        apiCallMadeRef.current || 
        requestMadeRef.current || 
        apiCallInProgress ||
        result === lastProcessedValue) {
      return;
    }
    
    // Use setTimeout to escape React's render cycle - this helps with StrictMode
    const timeoutId = setTimeout(() => {
      validateTicket(getIboNumber(result));
    }, 0);
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, [result, validateTicket]);
  
  // Reset everything when unmounting
  useEffect(() => {
    return () => {
      requestMadeRef.current = false;
      apiCallMadeRef.current = false;
      apiCallInProgress = false;
    };
  }, []);

  // Add this useEffect to handle retry logic
  useEffect(() => {
    // If we get "No validation data" but have a result, try again once
    if (!validationData && result && !shouldRetry && !loading && !error) {
      console.log("Setting up retry for validation...");
      
      // Mark that we're going to retry
      setShouldRetry(true);
      
      // Reset API call flags to allow retry
      apiCallMadeRef.current = false;
      requestMadeRef.current = false;
      apiCallInProgress = false;
      lastProcessedValue = null;
      
      // Wait a moment and retry the validation
      const retryTimer = setTimeout(() => {
        console.log("Retrying validation...");
        validateTicket(getIboNumber(result));
      }, 1500);
      
      return () => clearTimeout(retryTimer);
    }
  }, [validationData, result, loading, error, shouldRetry, validateTicket]);

  // Reset request tracking when scanning again
  const handleScanAgain = () => {
    // Reset all locks - both component-level and global
    requestMadeRef.current = false;
    apiCallMadeRef.current = false;
    apiCallInProgress = false;
    lastProcessedValue = null;
    
    // Navigate to scanner page instead of root (which is now dashboard)
    navigate('/scanner');
  };

  // Render validation result
  const renderValidationResult = () => {
    if (loading) {
      return <div className="validation-loading">{t('result.validating')}</div>;
    }

    if (error) {
      return (
        <div className="validation-error">
          <h3>{t('result.error.title')}</h3>
          <p>{error}</p>
          {String(rawErrorRef.current ?? error).includes("Cannot connect") && (
            <div className="api-connection-help">
              <p><strong>{t('result.connectionHelp.title')}</strong></p>
              <ul>
                <li>{t('result.connectionHelp.server', { url: 'http://localhost:8080' })}</li>
                <li>{t('result.connectionHelp.network')}</li>
                <li>{t('result.connectionHelp.cors')}</li>
                <li>{t('result.connectionHelp.token')}</li>
              </ul>
            </div>
          )}
        </div>
      );
    }

    // Add this debugging output to see what's in validationData
    console.log("Current validationData:", validationData);
    
    if (!validationData) {
      // If we're set to retry, show a better message
      if (shouldRetry) {
        return <div className="validation-retrying">{t('result.retrying')}</div>;
      }
      
      // Check for common API response issues
      if (result && !loading && !error) {
        return (
          <div className="no-validation">
            <p>{t('result.noData.title')}</p>
            <p className="validation-help">{t('result.noData.help')}</p>
            <button 
              onClick={() => {
                // Reset state to allow manual retry
                apiCallMadeRef.current = false;
                requestMadeRef.current = false;
                apiCallInProgress = false;
                lastProcessedValue = null;
                validateTicket(getIboNumber(result));
              }}
              className="retry-button"
            >
              {t('result.noData.retry')}
            </button>
          </div>
        );
      }
      
      return <div className="no-validation">{t('result.noData.short')}</div>;
    }

    // Safely access properties using optional chaining
    // Usually the event's own name; for a generic ticket the server makes one up in English
    // ("Generic CONFERENCE Event"), which the server-message catalog can translate.
    const eventName = translateServerMessage(validationData?.eventName, t('result.unknownEvent'));
    const iboNumber = validationData?.iboNumber || t('result.unknownIbo');
    const remainingTickets = validationData?.remainingTickets ?? 0;
    const valid = Boolean(validationData?.valid);
    const additionalInfo = translateServerMessage(validationData?.additionalInfo);
    // A ticket for another event is not the same as a bad ticket, and a volunteer needs to be able
    // to tell the difference at a glance — the holder has done nothing wrong and still has their
    // ticket. The server says which case it is rather than the screen guessing from the message.
    const wrongEvent = validationData?.rejectionReason === 'WRONG_EVENT';

    return (
      <div className={`validation-result ${valid ? 'valid-ticket' : 'invalid-ticket'}`}>
        <div className="validation-status">
          {valid ? (
            <div className="valid-badge">{t('result.badge.valid')}</div>
          ) : wrongEvent ? (
            <div className="invalid-badge">{t('result.badge.wrongEvent')}</div>
          ) : (
            <div className="invalid-badge">{t('result.badge.invalid')}</div>
          )}
        </div>
        
        <div className="validation-details">
          <div className="detail-row">
            <span className="detail-label">{t('result.labels.event')}</span>
            <span className="detail-value event-name">{eventName}</span>
          </div>
          
          <div className="detail-row">
            <span className="detail-label">{t('result.labels.ibo')}</span>
            <span className="detail-value">{iboNumber}</span>
          </div>
          
          <div className="detail-row">
            <span className="detail-label">{t('result.labels.remaining')}</span>
            <span className="detail-value">{remainingTickets}</span>
          </div>
          
          {additionalInfo && (
            <div className="detail-row special-info">
              <span className="detail-label">{t('result.labels.notice')}</span>
              <span className="detail-value additional-info">{additionalInfo}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="result-container">
      <h2>{t('result.title')}</h2>
      
      {result ? (
        <div className="validation-container">
          {renderValidationResult()}
        </div>
      ) : (
        <p>{t('result.noScan')}</p>
      )}
      
      <button 
        onClick={handleScanAgain}
        className="scan-again-button"
      >
        {t('result.scanAnother')}
      </button>
    </div>
  );
};

export default ResultPage;